import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Tabs, Tab, Button, Grid, 
  Card, CardContent, Chip, Stack,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress, Alert
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { DEMO_MODELS } from '../../helpers/MockData';
import PageLoading from '../../helpers/PageLoading';
import KeyManager from './KeyManager';
import RAGUpload from './RAGUpload';
import ApiUsage from './ApiUsage';
import ChatTab from './Chat';
import ModelsTab from './Model';
import DocumentationTab from './Documentation';
import { getModels, setApiKey, setApiEndpoint, getApiKeys, getFiles, getKeyUsageStats } from '../../api/services/agent-studio/AgentFeatures';
import { getAgentById, getAvailableEndpoints } from '../../api/services/agent-studio/AgentManagement';
import { useKeycloak } from '@react-keycloak/web';
import { theme } from '../../helpers/theme';

const ManageAgents = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [activeTab, setActiveTab] = useState(0);
  const [agent, setAgent] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [nodes, setNodes] = useState({});

  useEffect(() => {
    const fetchEndpoints = async () => {
      try {
        await keycloak.updateToken(30);
        const response = await getAvailableEndpoints(keycloak.token);
        if (response.success && response.data) {
          setEndpoints(response.data.endpoints || []);
          setNodes(response.data.nodes || {});
        }
      } catch (error) {
        console.error("Error fetching endpoints:", error);
      }
    };

    fetchEndpoints();
  }, [keycloak]);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        await keycloak.updateToken(30);
        const token = keycloak.token;
        const response = await getAgentById(token, agentId);
        if (response.success && response.data) {
          const agent = response.data.agent;
          setAgent({
            id: agent.id,
            name: agent.name,
            is_active: typeof agent.isActive === 'boolean' ? agent.isActive : false,
            status: agent.isActive ? 'active' : 'inactive',
            type: 'AI Agent',
            models: agent.models || [],
            model: agent.model || '',
            endpoint: agent.endpoint || '',
            language: agent.language || 'en',
            system_prompt: agent.system_prompt || '',
            domain: "main",
            apiKey: agent.apiKey || '',
            createdAt: agent.createdAt || '',
            updatedAt: agent.updatedAt || ''
          });
          
          if (agent.apiKey) {
            setApiKey(agent.apiKey);
            if (agent.endpoint) {
              setApiEndpoint(agent.endpoint);
              fetchModelsForEndpoint(agent.endpoint);
            }
          }
        } else {
          navigate('/agent-studio');
        }
      } catch (error) {
        navigate('/agent-studio');
      }
    };
    fetchAgent();
  }, [agentId, keycloak, navigate]);
  
  const fetchModelsForEndpoint = async (endpoint) => {
    try {
      const response = await getModels("dummy-key", endpoint);
      if (response.data) {
        const modelList = response.data.map(model => ({
          id: model.key,
          name: model.key.split('/').pop(),
          value: model.key,
          provider: model.key.split('/')[0]
        }));
        setAvailableModels(modelList);
      }
    } catch (error) {
      console.error("Error fetching models for endpoint:", error);
      setAvailableModels(DEMO_MODELS);
    }
  };

  if (!agent) {
    return <PageLoading />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Manage Agent: {agent.name}</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/agent-studio')}
            sx={{ 
              borderColor: 'rgba(255, 255, 255, 0.23)',
              color: 'white',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              }
            }}
          >
            Exit
          </Button>
        </Stack>
      </Box>

      <Tabs 
        value={activeTab} 
        onChange={(e, v) => setActiveTab(v)} 
        sx={{ 
          mb: 3,
          '& .MuiTab-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-selected': {
              color: theme.palette.primary.main
            }
          },
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.main
          }
        }}
      >
        <Tab label="OVERVIEW" />
        <Tab label="MODELS" />
        <Tab label="API KEYS" />
        <Tab label="DOCUMENTS" />
        <Tab label="CHAT" />
        <Tab label="USAGE" />
        <Tab label="DOCUMENTATION" />
      </Tabs>

      {activeTab === 0 && (
        <AgentOverview 
          agent={agent} 
          endpoints={endpoints}
          nodes={nodes}
          models={availableModels}
        />
      )}

      {activeTab === 1 && (
        <ModelsTab 
          apiKey={agent.apiKey}
          endpoint={agent.endpoint}
          selectedModel={agent.model}
        />
      )}

      {activeTab === 2 && (
        <KeyManager 
          apiKey={agent.apiKey} 
          agentId={agent.id} 
          endpoint={agent.endpoint}
        />
      )}

      {activeTab === 3 && (
        <RAGUpload 
          agentId={agent.id}
          apiKey={agent.apiKey}
          endpoint={agent.endpoint}
        />
      )}

      {activeTab === 4 && (
        <ChatTab 
          model={agent.model || (availableModels.length > 0 ? availableModels[0].value : "mistralai/Mistral-7B-Instruct-v0.3")}
          agentId={agent.id}
          endpoint={agent.endpoint}
          apiKey={agent.apiKey}
          availableModels={availableModels}
        />
      )}

      {activeTab === 5 && (
        <ApiUsage 
          agentId={agent.id}
          apiKey={agent.apiKey}
          endpoint={agent.endpoint}
        />
      )}

      {activeTab === 6 && (
        <DocumentationTab />
      )}
    </Box>
  );
};

const AgentOverview = ({ agent, endpoints, nodes, models }) => {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    totalKeys: 0,
    totalRequests: 0,
    totalTokens: 0,
    totalDocuments: 0,
    loading: true
  });

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const [keysResponse, filesResponse] = await Promise.all([
          getApiKeys(agent.apiKey, agent.endpoint).catch(() => ({ data: { keys: [] } })),
          getFiles(agent.apiKey, 1, agent.endpoint).catch(() => ({ data: { files: [] } }))
        ]);

        const keys = keysResponse.data.keys || [];
        let totalRequests = 0;
        let totalTokens = 0;

        if (keys.length > 0) {
          const usagePromises = keys.map(key => 
            getKeyUsageStats(key.id, agent.apiKey, agent.endpoint).catch(() => ({ data: { total_requests: 0, total_tokens: 0 } }))
          );
          
          const usageResults = await Promise.all(usagePromises);
          totalRequests = usageResults.reduce((sum, result) => sum + (result.data.total_requests || 0), 0);
          totalTokens = usageResults.reduce((sum, result) => sum + (result.data.total_tokens || 0), 0);
        }

        let totalDocuments = 0;
        if (filesResponse.data && filesResponse.data.files) {
          let allFiles = [...filesResponse.data.files];
          let currentPage = 2;
          let hasMorePages = filesResponse.data.files.length > 0;

          while (hasMorePages) {
            try {
              const nextPageResponse = await getFiles(agent.apiKey, currentPage, agent.endpoint);
              const nextPageFiles = nextPageResponse.data.files;
              
              if (!nextPageFiles || nextPageFiles.length === 0) {
                hasMorePages = false;
              } else {
                allFiles = [...allFiles, ...nextPageFiles];
                currentPage++;
              }
            } catch (error) {
              hasMorePages = false;
            }
          }
          totalDocuments = allFiles.length;
        }

        setDashboardStats({
          totalKeys: keys.length,
          totalRequests,
          totalTokens,
          totalDocuments,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        setDashboardStats(prev => ({ ...prev, loading: false }));
      }
    };

    if (agent.apiKey && agent.endpoint) {
      fetchDashboardStats();
    }
  }, [agent.apiKey, agent.endpoint]);

  const handleDelete = async () => {
    setDeleteDialogOpen(false);
    navigate('/agent-studio');
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getNodeInfo = (endpoint) => {
    if (!endpoint || !nodes || !nodes[endpoint]) return null;
    return nodes[endpoint];
  };

  const nodeInfo = getNodeInfo(agent.endpoint);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={{ 
          backgroundColor: theme.palette.background.paper,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
                  {agent.name}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                  <Chip 
                    label={agent.is_active ? "ACTIVE" : "INACTIVE"}
                    sx={{ 
                      backgroundColor: agent.is_active ? theme.palette.primary.main : '#ff9800', 
                      color: 'black', 
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                    size="small"
                  />
                  <Chip 
                    label="Production"
                    sx={{ 
                      backgroundColor: 'rgba(76, 175, 80, 0.8)', 
                      color: 'white', 
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                    size="small"
                  />
                </Stack>
              </Box>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
                  Configuration Details
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: 3,
                  p: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Agent ID
                    </Typography>
                    <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {agent.id}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Type
                    </Typography>
                    <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {agent.type}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Model
                    </Typography>
                    <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {agent.model ? agent.model.split('/').pop() : 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Language
                    </Typography>
                    <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {agent.language === 'en' ? 'English' : 'German'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Created
                    </Typography>
                    <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {formatDate(agent.createdAt)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Updated
                    </Typography>
                    <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {formatDate(agent.updatedAt)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
                  Infrastructure
                </Typography>
                {nodeInfo ? (
                  <Box sx={{ 
                    p: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.05)'
                  }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                          Endpoint
                        </Typography>
                        <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                          {agent.endpoint}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                          Location
                        </Typography>
                        <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                          {nodeInfo.location}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                          GPU Type
                        </Typography>
                        <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                          {nodeInfo.gpu}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                          GPU Count
                        </Typography>
                        <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                          {nodeInfo.noOfGpu}x {nodeInfo.gpu}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    p: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    textAlign: 'center'
                  }}>
                    <Typography color="text.secondary">
                      No infrastructure information available
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
          Quick Statistics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              backgroundColor: theme.palette.background.paper,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" sx={{ 
                  color: theme.palette.primary.main, 
                  fontWeight: 700,
                  mb: 1
                }}>
                  {dashboardStats.loading ? (
                    <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
                  ) : (
                    dashboardStats.totalKeys
                  )}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                  API Keys
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  Total active keys
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              backgroundColor: theme.palette.background.paper,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" sx={{ 
                  color: '#2196f3', 
                  fontWeight: 700,
                  mb: 1
                }}>
                  {dashboardStats.loading ? (
                    <CircularProgress size={32} sx={{ color: '#2196f3' }} />
                  ) : (
                    dashboardStats.totalRequests.toLocaleString()
                  )}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                  Total Requests
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  API calls made
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              backgroundColor: theme.palette.background.paper,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" sx={{ 
                  color: '#ff9800', 
                  fontWeight: 700,
                  mb: 1
                }}>
                  {dashboardStats.loading ? (
                    <CircularProgress size={32} sx={{ color: '#ff9800' }} />
                  ) : (
                    dashboardStats.totalTokens.toLocaleString()
                  )}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                  Total Tokens
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  Tokens processed
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              backgroundColor: theme.palette.background.paper,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" sx={{ 
                  color: '#4caf50', 
                  fontWeight: 700,
                  mb: 1
                }}>
                  {dashboardStats.loading ? (
                    <CircularProgress size={32} sx={{ color: '#4caf50' }} />
                  ) : (
                    dashboardStats.totalDocuments
                  )}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                  Documents
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  RAG documents
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this agent? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ManageAgents;
